import { ARIA, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/** Pixels scrolled per second during auto-play. Tune here. */
const PX_PER_SECOND = 40;
const DRAG_THRESHOLD = 5;

/**
 * PartnershipCarousel — marquee-style carousel that duplicates slides and
 * translates the wrapper to create a seamless looping carousel.
 * @class PartnershipCarousel
 * @extends MessageBus
 * @description Duplicates slides once and advances a single `scrollX` value
 * to produce continuous scrolling. Supports auto-play, wheel scrolling, and
 * pointer dragging.
 */
export default class PartnershipCarousel extends MessageBus {
  /**
   * @param {HTMLElement} element - the module's container
   */
  constructor(element) {
    super();

    /** @type {HTMLElement} */
    this.element = element;
    this.scrollX = 0;
    this.originalWidth = 0;
    this.originalCount = 0;
    this.isPaused = ARIA.FALSE;
    this.dragPhase = EVENTS.IDLE;
    this.dragStartX = 0;
    this.dragStartScrollX = 0;
    this.activePointerId = null;

    /**
     * True when the pointer was released after a confirmed drag.
     * Used to suppress the click event the browser fires after pointerup.
     */
    this.didDrag = ARIA.FALSE;
    this.rafId = null;
    this.init();
  }

  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  cacheDomReferences() {
    this.trackEl = this.element.querySelector(
      SELECTORS.PARTNERSHIP_CAROUSEL_TRACK
    );

    this.wrapperEl = this.trackEl?.querySelector(
      SELECTORS.PARTNERSHIP_CAROUSEL_WRAPPER
    );

    return this;
  }

  setupHandlers() {
    this.handleMouseEnter = this.onMouseEnter.bind(this);
    this.handleMouseLeave = this.onMouseLeave.bind(this);
    this.handleWheel = this.onWheel.bind(this);
    this.handlePointerDown = this.onPointerDown.bind(this);
    this.handlePointerMove = this.onPointerMove.bind(this);
    this.handlePointerUp = this.onPointerUp.bind(this);
    this.handleClick = this.onClick.bind(this);

    return this;
  }

  enable() {
    if (!this.trackEl || !this.wrapperEl) return this;

    this.duplicateSlides();
    this.computeOriginalWidth();
    this.bindEvents();
    this.startScroll();

    return this;
  }

  destroy() {
    cancelAnimationFrame(this.rafId);
    this.trackEl.removeEventListener(EVENTS.MOUSEENTER, this.handleMouseEnter);
    this.trackEl.removeEventListener(EVENTS.MOUSELEAVE, this.handleMouseLeave);
    this.trackEl.removeEventListener(EVENTS.WHEEL, this.handleWheel);
    this.trackEl.removeEventListener(
      EVENTS.POINTERDOWN,
      this.handlePointerDown
    );
    this.trackEl.removeEventListener(
      EVENTS.POINTERMOVE,
      this.handlePointerMove
    );
    this.trackEl.removeEventListener(EVENTS.POINTERUP, this.handlePointerUp);
    this.trackEl.removeEventListener(
      EVENTS.POINTERCANCEL,
      this.handlePointerUp
    );
    this.trackEl.removeEventListener(EVENTS.CLICK, this.handleClick);

    return this;
  }

  /**
   * Appends a cloned copy of every original slide to the wrapper.
   * @returns {this} The instance for chaining.
   */
  duplicateSlides() {
    const originals = Array.from(this.wrapperEl.children);
    this.originalCount = originals.length;

    originals.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', ARIA.TRUE);
      clone.querySelectorAll('[tabindex], a, button, input').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      this.wrapperEl.appendChild(clone);
    });

    return this;
  }

  /**
   * Measures the pixel distance that equals one full scroll cycle.
   * originalWidth = Σ(slideWidths) + originalCount × gap
   * @returns {this} The instance for chaining.
   */
  computeOriginalWidth() {
    const gap =
      parseFloat(window.getComputedStyle(this.wrapperEl).columnGap) || 0;

    const originals = Array.from(this.wrapperEl.children).slice(
      0,
      this.originalCount
    );

    this.originalWidth =
      originals.reduce((total, slide) => total + slide.offsetWidth, 0) +
      this.originalCount * gap;

    return this;
  }

  /**
   * Normalises any scroll value into [0, originalWidth) using modular arithmetic.
   * @param {number} value - input scroll value in pixels
   * @returns {number} Normalised scroll value in range [0, originalWidth)
   */
  wrapScroll(value) {
    return (
      ((value % this.originalWidth) + this.originalWidth) % this.originalWidth
    );
  }

  applyTransform() {
    this.wrapperEl.style.transform = `translateX(${-this.scrollX}px)`;
  }

  onMouseEnter() {
    this.isPaused = ARIA.TRUE;
  }

  onMouseLeave() {
    this.isPaused = ARIA.FALSE;
  }

  /**
   * Handle wheel/trackpad events and update scroll position.
   * @param {WheelEvent} event - The wheel or trackpad event.
   */
  onWheel(event) {
    event.preventDefault();

    const delta =
      Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;

    this.scrollX = this.wrapScroll(this.scrollX + delta);
    this.applyTransform();
  }

  /**
   * Begin pointer interaction; don't capture yet until drag threshold is crossed.
   * @param {PointerEvent} event - The pointerdown event initiating interaction.
   */
  onPointerDown(event) {
    this.dragPhase = EVENTS.PENDING;
    this.didDrag = ARIA.FALSE;
    this.dragStartX = event.clientX;
    this.dragStartScrollX = this.scrollX;
    this.activePointerId = event.pointerId;
  }

  /**
   * Track pointer movement; commit to drag after threshold.
   * @param {PointerEvent} event - The pointermove event while interacting.
   */
  onPointerMove(event) {
    if (this.dragPhase === EVENTS.IDLE) return;

    const distance = Math.abs(event.clientX - this.dragStartX);

    if (this.dragPhase === EVENTS.PENDING) {
      if (distance < DRAG_THRESHOLD) return;

      this.dragPhase = EVENTS.DRAGGING;
      this.trackEl.setPointerCapture(this.activePointerId);
    }

    const delta = this.dragStartX - event.clientX;
    this.scrollX = this.wrapScroll(this.dragStartScrollX + delta);
    this.applyTransform();
  }

  /**
   * End pointer interaction; mark didDrag if a real drag occurred.
   */
  onPointerUp() {
    if (this.dragPhase === EVENTS.DRAGGING) {
      this.didDrag = ARIA.TRUE;
    }

    this.dragPhase = EVENTS.IDLE;
    this.activePointerId = null;
  }

  /**
   * Suppress the synthetic click event fired after a drag.
   * @param {MouseEvent} event - The click event that may be suppressed after a drag.
   */
  onClick(event) {
    if (this.didDrag) {
      event.stopPropagation();
      this.didDrag = ARIA.FALSE;
    }
  }

  bindEvents() {
    this.trackEl.addEventListener(EVENTS.MOUSEENTER, this.handleMouseEnter);
    this.trackEl.addEventListener(EVENTS.MOUSELEAVE, this.handleMouseLeave);
    this.trackEl.addEventListener(EVENTS.WHEEL, this.handleWheel, {
      passive: false
    });
    this.trackEl.addEventListener(EVENTS.POINTERDOWN, this.handlePointerDown);
    this.trackEl.addEventListener(EVENTS.POINTERMOVE, this.handlePointerMove);
    this.trackEl.addEventListener(EVENTS.POINTERUP, this.handlePointerUp);
    this.trackEl.addEventListener(EVENTS.POINTERCANCEL, this.handlePointerUp);
    this.trackEl.addEventListener(EVENTS.CLICK, this.handleClick, true);

    return this;
  }

  startScroll() {
    let lastTimestamp = null;

    const tick = (timestamp) => {
      this.rafId = requestAnimationFrame(tick);
      if (this.isPaused || this.dragPhase !== EVENTS.IDLE) {
        lastTimestamp = null;
        return;
      }

      if (lastTimestamp !== null) {
        const elapsed = timestamp - lastTimestamp;
        const delta = (PX_PER_SECOND * elapsed) / 1000;
        this.scrollX = this.wrapScroll(this.scrollX + delta);
        this.applyTransform();
      }

      lastTimestamp = timestamp;
    };

    this.rafId = requestAnimationFrame(tick);

    return this;
  }
}
